import fileDownload from 'js-file-download';
import { apiSlice } from '../apiSlice';
import { RiskLevelEnum } from '../enum/common.enum';

export interface InterventionReportFilter {
	staffIds?: string[];
	dateFrom?: string;
	dateTo?: string;
	riskLevels?: RiskLevelEnum[];
	userIds?: string[];
}

export interface CaseWorkerStatisticsReportFilter {
	reportMonth: string;
	staffId?: string;
}

export interface SpsFormReportFilter {
	financialYear: number;
	quarter: number;
}

const safeDecode = (value: string) => {
	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
};

// Prefer Content-Disposition (filename* > filename); fall back to a default name.
const getFileName = (response: Response, fallback: string) => {
	const disposition = response.headers.get('Content-Disposition') || '';
	const encoded = /filename\*\s*=\s*[^']*'[^']*'([^;]+)/i.exec(
		disposition
	)?.[1];
	if (encoded) return safeDecode(encoded.trim().replace(/^"|"$/g, ''));
	const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition)?.[1];
	if (plain) return plain.trim();
	return fallback;
};

// Saves the workbook on success; on failure returns the JSON error envelope so
// the shared baseQuery can surface its message.
const downloadHandler =
	(fallbackName: string) => async (response: Response) => {
		if (!response.ok) {
			try {
				return await response.json();
			} catch {
				return { success: false, message: response.statusText };
			}
		}
		fileDownload(await response.blob(), getFileName(response, fallbackName));
		return null;
	};

// Arrays are sent comma separated; empty values are omitted.
const toQueryString = (params: object) => {
	const search = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value == null || value === '') return;
		if (Array.isArray(value)) {
			if (value.length > 0) search.set(key, value.join(','));
			return;
		}
		search.set(key, String(value));
	});
	const query = search.toString();
	return query ? `?${query}` : '';
};

export const reportApi = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		exportAllMemberReport: builder.mutation<null, void>({
			query: () => ({
				url: `/web/report/all-member-data/export`,
				method: 'GET',
				responseHandler: downloadHandler('All_Member_Data.xlsx'),
				cache: 'no-cache',
			}),
		}),
		exportActiveFamilyMemberReport: builder.mutation<null, void>({
			query: () => ({
				url: `/web/report/active-family-member/export`,
				method: 'GET',
				responseHandler: downloadHandler('Active_Family_Member.xlsx'),
				cache: 'no-cache',
			}),
		}),
		exportInterventionReport: builder.mutation<null, InterventionReportFilter>({
			query: (filter) => ({
				url: `/web/report/intervention/export${toQueryString(filter)}`,
				method: 'GET',
				responseHandler: downloadHandler('Intervention_Report.xlsx'),
				cache: 'no-cache',
			}),
		}),
		exportCaseWorkerStatisticsReport: builder.mutation<
			null,
			CaseWorkerStatisticsReportFilter
		>({
			query: (filter) => ({
				url: `/web/report/case-worker-statistics/export${toQueryString(
					filter
				)}`,
				method: 'GET',
				responseHandler: downloadHandler(
					`case_worker_statistics_${filter.reportMonth}.xlsx`
				),
				cache: 'no-cache',
			}),
		}),
		exportOpForm18aReport: builder.mutation<
			null,
			CaseWorkerStatisticsReportFilter
		>({
			query: (filter) => ({
				url: `/web/report/op-form-18a/export${toQueryString(filter)}`,
				method: 'GET',
				responseHandler: downloadHandler(
					`op_form_18a_${filter.reportMonth}.xlsx`
				),
				cache: 'no-cache',
			}),
		}),
		exportSpsFormReport: builder.mutation<null, SpsFormReportFilter>({
			query: (filter) => ({
				url: `/web/report/sps-form/export${toQueryString(filter)}`,
				method: 'GET',
				responseHandler: downloadHandler(
					`sps_form_${filter.financialYear}-${String(
						(filter.financialYear + 1) % 100
					).padStart(2, '0')}_Q${filter.quarter}.xlsx`
				),
				cache: 'no-cache',
			}),
		}),
	}),
});

export const {
	useExportAllMemberReportMutation,
	useExportActiveFamilyMemberReportMutation,
	useExportInterventionReportMutation,
	useExportCaseWorkerStatisticsReportMutation,
	useExportOpForm18aReportMutation,
	useExportSpsFormReportMutation,
} = reportApi;
